import { REACTION_SETS } from "./reactions";

/**
 * Custom (per-organization) reaction sets.
 *
 * A liveblog's `reaction_set_id` holds EITHER a bare predefined slug
 * (e.g. "news" — resolved against REACTION_SETS with i18n labels) OR a
 * self-contained snapshot of a custom set encoded as:
 *
 *   "crs1:" + base64url(JSON{ v:1, sid, n, o:[{ i, e, l }] })
 *
 * Nothing else in the liveblog data model changes; the reader decodes the
 * snapshot and the interactive-backend continues to treat the per-option
 * reaction id (`i`) as an opaque `[^#]+` string.
 */

export const CUSTOM_REACTION_SET_PREFIX = "crs1:";

// A snapshot travels in `Game.reaction_set_id`, which the write model accepts as an arbitrary
// string — so a decoded snapshot is UNTRUSTED INPUT even though the encoder only ever produces
// it from Zod-validated org settings. Decoding must therefore re-assert every constraint the
// org-settings schema enforces (webng/types/editor/organization-settings.ts), or a crafted
// `crs1:` value would let an editor bypass them. Keep these in sync with that schema.
const MAX_SNAPSHOT_OPTIONS = 6;
const MAX_SNAPSHOT_LABEL_LENGTH = 40;
// `#` separates the composite reaction keys used by interactive-backend
// (`userId#eventId#reactionId`) and change-bridge (which splits count keys on it), so a `#`
// inside a reaction id would corrupt those keys and mis-attribute counts across events.
const SAFE_REACTION_ID = /^[^#]+$/;

export interface CustomReactionOption {
  id: string;
  emoji: string;
  label: string;
}

export interface DecodedReactionSnapshot {
  /** id of the org custom set this was snapshotted from */
  sid: string;
  /** set name at snapshot time (for editor display) */
  name: string;
  options: CustomReactionOption[];
}

/** Minimal shape needed to encode a snapshot (structurally satisfied by the editor's `ReactionSet`). */
export interface EncodableReactionSet {
  id: string;
  name: string;
  options: CustomReactionOption[];
}

interface EncodedPayload {
  v: 1;
  sid: string;
  n: string;
  o: { i: string; e: string; l: string }[];
}

function toBase64Url(str: string): string {
  const bytes = new TextEncoder().encode(str);
  let binary = "";
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function fromBase64Url(value: string): string {
  const base64 = value.replace(/-/g, "+").replace(/_/g, "/");
  const binary = atob(base64);
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

/** True when `value` is one of the built-in predefined set slugs. */
export function isPredefinedId(value: string | undefined | null): boolean {
  return !!value && REACTION_SETS.some((set) => set.id === value);
}

/** True when `value` is an encoded custom-set snapshot. */
export function isEncodedCustom(value: string | undefined | null): boolean {
  return !!value && value.startsWith(CUSTOM_REACTION_SET_PREFIX);
}

/** Encode a custom set into a self-contained `crs1:` snapshot string. */
export function encodeReactionSet(set: EncodableReactionSet): string {
  const payload: EncodedPayload = {
    v: 1,
    sid: set.id,
    n: set.name,
    o: set.options.map((option) => ({ i: option.id, e: option.emoji, l: option.label })),
  };
  return CUSTOM_REACTION_SET_PREFIX + toBase64Url(JSON.stringify(payload));
}

/** Decode a `crs1:` snapshot to its full form (with set identity + name), or null if invalid. */
export function decodeReactionSetSnapshot(value: string | undefined | null): DecodedReactionSnapshot | null {
  if (!isEncodedCustom(value)) {
    return null;
  }
  try {
    const json = fromBase64Url((value as string).slice(CUSTOM_REACTION_SET_PREFIX.length));
    const payload = JSON.parse(json) as unknown;
    if (typeof payload !== "object" || payload === null) {
      return null;
    }
    const { v, sid, n, o } = payload as Record<string, unknown>;
    if (v !== 1 || typeof sid !== "string" || typeof n !== "string" || !Array.isArray(o)) {
      return null;
    }
    if (o.length > MAX_SNAPSHOT_OPTIONS) {
      return null;
    }
    const options: CustomReactionOption[] = [];
    const seenIds = new Set<string>();
    for (const raw of o) {
      if (typeof raw !== "object" || raw === null) {
        return null;
      }
      const { i, e, l } = raw as Record<string, unknown>;
      if (typeof i !== "string" || typeof e !== "string" || typeof l !== "string") {
        return null;
      }
      // mirrors reactionOptionSchema: non-empty id without "#", non-empty emoji, bounded label
      if (i.length === 0 || !SAFE_REACTION_ID.test(i) || e.length === 0 || l.length > MAX_SNAPSHOT_LABEL_LENGTH) {
        return null;
      }
      // A labelless option is SKIPPED, not fatal. Such options were storable before the label rule
      // was tightened, and rejecting the whole snapshot over one of them would silently remove
      // reactions from every liveblog using that set — far worse than dropping the one option that
      // has nothing to render as its title. New writes can't introduce these
      // (organizationSettingsWriteSchemas enforces a non-empty label).
      if (l.trim().length === 0) {
        continue;
      }
      // duplicate ids would share a counter key and a React key, so both buttons would move
      // together and the second click would 409
      if (seenIds.has(i)) {
        return null;
      }
      seenIds.add(i);
      options.push({ id: i, emoji: e, label: l });
    }
    if (options.length === 0) {
      return null;
    }
    return { sid, name: n, options };
  } catch {
    return null;
  }
}

/** Decode a `crs1:` snapshot to its reader-facing options, or null if invalid. */
export function decodeReactionSet(value: string | undefined | null): CustomReactionOption[] | null {
  return decodeReactionSetSnapshot(value)?.options ?? null;
}

/**
 * Resolve a `reaction_set_id` to the reader-facing options to render.
 * - predefined slug → the built-in set, labels resolved via `translate(i18nKey)`
 * - `crs1:` snapshot → the decoded options with their literal labels
 * - anything else (unknown/malformed) → [] (render nothing)
 */
export function resolveReactionOptions(
  reactionSetId: string | undefined | null,
  translate: (i18nKey: string) => string,
): CustomReactionOption[] {
  const predefined = REACTION_SETS.find((set) => set.id === reactionSetId);
  if (predefined) {
    return predefined.options.map((option) => ({ id: option.id, emoji: option.emoji, label: translate(option.i18nKey) }));
  }
  return decodeReactionSet(reactionSetId) ?? [];
}

const REACTION_ID_ALPHABET = "abcdefghijklmnopqrstuvwxyz0123456789";

/**
 * Generate a stable, slug-safe reaction id. Contains no "#" so it satisfies the
 * reaction/count key format enforced by the change-bridge and interactive-backend.
 */
export function generateReactionId(): string {
  let id = "r";
  for (let index = 0; index < 8; index++) {
    id += REACTION_ID_ALPHABET.charAt(Math.floor(Math.random() * REACTION_ID_ALPHABET.length));
  }
  return id;
}

// --- Org-settings-facing helpers ---
// These use structural types (not `@webng-types/editor`, which this lib does not
// depend on); the editor's `ReactionSet`/`ReactionSetsSetting` are structurally
// identical, so they interoperate without a cross-package type dependency.

export interface OrgReactionSet {
  id: string;
  name: string;
  active: boolean;
  options: CustomReactionOption[];
}

export interface OrgReactionSetsSetting {
  sets: OrgReactionSet[];
  hiddenPresets: string[];
}

/** A built-in preset surfaced to the editor, flagged with the org's hidden state. */
export interface EffectivePreset {
  slug: string;
  i18nKey: string;
  options: { id: string; emoji: string; i18nKey: string }[];
  hidden: boolean;
}

export interface EffectiveSets<T extends OrgReactionSet = OrgReactionSet> {
  customs: T[];
  presets: EffectivePreset[];
}

const EMPTY_SETTING: OrgReactionSetsSetting = { sets: [], hiddenPresets: [] };

/**
 * Merge the org's stored custom sets with the built-in presets. Presets stay
 * separate (no shadowing); each carries a `hidden` flag from `hiddenPresets`.
 */
export function resolveEffectiveSets<T extends OrgReactionSet>(
  setting: { sets: T[]; hiddenPresets: string[] } | null | undefined,
): EffectiveSets<T> {
  const { sets, hiddenPresets } = setting ?? (EMPTY_SETTING as { sets: T[]; hiddenPresets: string[] });
  return {
    customs: sets,
    presets: REACTION_SETS.map((preset) => ({
      slug: preset.id,
      i18nKey: preset.i18nKey,
      options: preset.options,
      hidden: hiddenPresets.includes(preset.id),
    })),
  };
}

/**
 * Build an independent, editable custom set from a preset. Labels (set name and
 * per-option labels) are resolved to concrete text via the injected resolver
 * (typically `(key) => t(key, {ns: "liveblog"})`) so the copy is self-contained.
 * The copy has fresh generated ids and no link back to the preset.
 */
export function copyPreset(presetSlug: string, resolveLabel: (i18nKey: string) => string): OrgReactionSet | null {
  const preset = REACTION_SETS.find((set) => set.id === presetSlug);
  if (!preset) {
    return null;
  }
  return {
    id: generateReactionId(),
    name: resolveLabel(preset.i18nKey),
    active: true,
    options: preset.options.map((option) => ({
      id: generateReactionId(),
      emoji: option.emoji,
      label: resolveLabel(option.i18nKey),
    })),
  };
}

/** A blank custom set for the "New set" action. */
export function newReactionSet(): OrgReactionSet {
  return {
    id: generateReactionId(),
    name: "",
    active: true,
    options: [],
  };
}

/**
 * Resolve an org's `editor-defaults.defaultReactionSet` handle to the value a newly created
 * liveblog should store: a preset stays a bare slug, a custom set is snapshot-encoded from the
 * set as it exists right now.
 *
 * Shared by the create form (which seeds react-hook-form) and the generics form (which re-applies
 * the default if it only becomes resolvable after mount), so the two can never disagree.
 *
 * Returns `undefined` — "no default" — when the reactions capability is not known-enabled (this
 * mirrors the submit-time capability check, so what the form shows and what it submits always
 * agree), or when the handle points at something no longer on offer: a deactivated custom set, a
 * hidden preset, or a slug that no longer resolves.
 */
export function resolveDefaultReactionSetId(
  handle: string | undefined,
  reactionSets: OrgReactionSetsSetting | null | undefined,
  canUseReactions: boolean,
): string | undefined {
  if (!canUseReactions || !handle) {
    return undefined;
  }
  if (handle.startsWith("custom:")) {
    const sid = handle.slice("custom:".length);
    const set = reactionSets?.sets.find((candidate) => candidate.id === sid && candidate.active);
    return set ? encodeReactionSet(set) : undefined;
  }
  const hiddenPresets = reactionSets?.hiddenPresets ?? [];
  if (!isPredefinedId(handle) || hiddenPresets.includes(handle)) {
    return undefined;
  }
  return handle;
}
